import { useState } from "react";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  PencilIcon,
  SlidersHorizontalIcon,
  type LucideIcon,
} from "lucide-react";

import { TableColumnType } from "@/components/@types";
import { getGridCellBorderEdges } from "@/components/organisms/Table/Table.tools";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { ZIndex, Values } from "@/constants";
import { List } from "@/components/organisms/List";
import { createFilterFromDefinition } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import {
  useTableStore,
  useTableStoreApi,
} from "@/components/organisms/Table/Table.store";
import { useScope } from "@/hooks/useScope";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";

export function TableHeaderCell({
  column,
  index,
  isLastColumn = false,
  hideOuterBorders = false,
  layoutIndex,
  getClassName,
}: {
  column: TableColumnType;
  index: number;
  isLastColumn?: boolean;
  hideOuterBorders?: boolean;
  layoutIndex?: number;
  getClassName: (objectKey: string) => string;
}) {
  // Its foot and its right edge - the column's, drawn here alone, over its
  // own ground as the pointer lights it (what holds it draws none: drawn in
  // both, the two lay one over the other, a divider twice the rows').
  const borderEdges = getGridCellBorderEdges({
    color: "var(--border)",
    isOuterRight: isLastColumn,
    hideOuterEdges: hideOuterBorders,
  });
  const { entities, activeEntityIdentifiers } = useScope();
  const storeApi = useTableStoreApi();
  const [isOpen, setIsOpen] = useState(false);
  // The open list's filter on this column, keyed as its chips are.
  const filterKey = column.inputKey || column.objectKey;
  const filterDefinition = State((state) => state.filterDefinitions[filterKey]);

  return (
    <div
      style={{
        position: "relative",
        zIndex: ZIndex.GRID.HEADER_CELL,
      }}
      key={column.objectKey}
      data-testid={`table-header-cell-${column.objectKey}`}
    >
      <Popper
        isOpenState={[isOpen, setIsOpen]}
        anchor={
          <div
            className={getClassName(column.objectKey)}
            style={{
              position: "relative",
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "flex-start",
              height: Values.DEFAULT_HEADER_HEIGHT_PX,
              maxHeight: Values.DEFAULT_HEADER_HEIGHT_PX,
              ...borderEdges,
              cursor: "pointer",
              paddingRight: "8px",
              paddingLeft: "10px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.backgroundColor = "var(--accent)";
            }}
          >
            {column?.ReactIcon && (
              <Icon
                ReactIcon={column.ReactIcon}
                size="sm"
                style={{
                  minWidth: (Values.DEFAULT_ROW_HEIGHT * 2) / 4 + "px",
                  maxWidth: Values.DEFAULT_ROW_HEIGHT / 2 + "px",
                  color: "var(--muted-foreground)",
                  marginRight: 4,
                }}
              />
            )}
            <Typography
              key={index}
              level="body-md"
              style={{
                alignItems: "center",
                textWrap: "nowrap",
                color: "var(--foreground)",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
              className="truncate"
            >
              {column?.label}
            </Typography>
          </div>
        }
        placement="bottom-start"
        style={{ zIndex: ZIndex.GRID.HEADER_CHECKBOX }}
        floatingStyle={{
          minWidth: "320px",
        }}
      >
        <List
          data={[
            column?.getEditableObjectInfo != null && {
              key: "edit",
              label: "Edit",
              ReactIcon: PencilIcon,
              onClick: () => {
                let typename = "";
                let commandDefinitionId: string | undefined;
                const nodeAndEntityIds = [];
                for (const activeEntityIdentifer of activeEntityIdentifiers) {
                  const activeEntity = entities?.find(
                    (entity) => entity.identifier === activeEntityIdentifer,
                  );
                  if (!activeEntity) continue;
                  if (!column?.getEditableObjectInfo) continue;

                  const {
                    typename: typename_,
                    id: id_,
                    commandDefinitionId: command_,
                  } = column.getEditableObjectInfo(activeEntity, activeEntity);

                  if (id_ && (typename_ || command_)) {
                    typename = typename_;
                    commandDefinitionId = command_;
                    nodeAndEntityIds.push({
                      entityId: activeEntity.id,
                      nodeId: id_,
                    });
                  }
                }
                if (nodeAndEntityIds.length > 0) {
                  const firstNode = nodeAndEntityIds[0];
                  State.getState().pushToCommandStack({
                    commandDefinitionId:
                      commandDefinitionId ?? `update-${typename}`,
                    context: {
                      nodeId: firstNode.nodeId,
                      entityId: firstNode.entityId,
                      entityIdentifier: idToIdentifier(firstNode.entityId),
                    },
                  });
                }
              },
            },
            column?.isSortable && {
              key: "sort-ascending",
              label: "Sort ascending",
              ReactIcon: ArrowUpIcon,
              onClick: () => {
                State.setState({
                  sortables: [
                    {
                      key: column.objectKey,
                      value: "ASC",
                    },
                  ],
                });
              },
            },
            column?.isSortable && {
              key: "sort-descending",
              label: "Sort descending",
              ReactIcon: ArrowDownIcon,
              onClick: () => {
                State.setState({
                  sortables: [
                    {
                      key: column.objectKey,
                      value: "DESC",
                    },
                  ],
                });
              },
            },
            filterDefinition != null && {
              key: "filter",
              label: "Filter",
              ReactIcon: SlidersHorizontalIcon,
              // To the column's chip - a new one when it has none - opened at
              // what there is to fill in. After this menu has closed, so it
              // does not take back the focus the chip's picker is given.
              onClick: () => {
                setTimeout(() => {
                  const { filters } = State.getState();
                  State.setState({
                    filters: filters.some((filter) => filter.key === filterKey)
                      ? filters
                      : [
                          ...filters,
                          createFilterFromDefinition(
                            filterKey,
                            filterDefinition,
                          ),
                        ],
                    openFilterKey: filterKey,
                  });
                }, 0);
              },
            },
            {
              key: "move-left",
              label: "Move left",
              ReactIcon: ArrowLeftIcon,
              onClick: () => {
                if (layoutIndex !== undefined) {
                  storeApi.getState().shiftLayoutIndex(layoutIndex, -1);
                }
              },
            },
            {
              key: "move-right",
              label: "Move right",
              ReactIcon: ArrowRightIcon,
              onClick: () => {
                if (layoutIndex !== undefined) {
                  storeApi.getState().shiftLayoutIndex(layoutIndex, 1);
                }
              },
            },
          ].filter(Boolean)}
          onItemClick={(row) => {
            setIsOpen(false);
            row.onClick();
          }}
        >
          <List.Items>
            {(row: {
              key: string;
              label: string;
              ReactIcon: LucideIcon;
              onClick: () => void;
              isDivider?: boolean;
            }) => (
              <List.Item
                value={row}
                data-testid={`table-header-action-${row.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Icon ReactIcon={row?.ReactIcon} size="sm" style={{}} />
                  <Typography>{row.label}</Typography>
                </div>
              </List.Item>
            )}
          </List.Items>
        </List>
      </Popper>
    </div>
  );
}
